<template>
  <!--
    Workaround because it's not possible to make the gray border larger than the RevTabs itself
    https://imperatorz.slack.com/archives/CFMDV68KA/p1709572377044399?thread_ts=1709552811.539399&cid=CFMDV68KA
  -->
  <div
    class="relative after:border-static-default-low after:absolute after:bottom-0 after:mb-8 after:w-full after:border-b"
  >
    <RevTabs
      v-if="!onboardingCompleted"
      class="mx-8"
      :label="i18n(translations.tabsLabel)"
      role="navigation"
      scrollable
      variant="flexible"
    >
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.HOME.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.ONBOARDING.HOME)"
        class="capitalize"
        :data-qa="translations.onboardingHome.id"
        :label="i18n(translations.onboardingHome)"
        role="tab"
        :to="{ name: ROUTE_NAMES.ONBOARDING.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.LISTINGS.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(getOnboardingListingsRoute())"
        class="capitalize"
        :data-qa="translations.listings.id"
        :label="i18n(translations.listings)"
        role="tab"
        :to="{
          name: getOnboardingListingsRoute(),
        }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.OPTIONS.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.ONBOARDING.OPTIONS.INDEX)"
        class="capitalize"
        :data-qa="translations.options.id"
        :label="i18n(translations.options)"
        role="tab"
        :to="{ name: ROUTE_NAMES.ONBOARDING.OPTIONS.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.ONBOARDING.SUPPORT)"
        :active="isActiveTab(ROUTE_NAMES.ONBOARDING.SUPPORT)"
        class="capitalize"
        :data-qa="translations.support.id"
        :label="i18n(translations.support)"
        role="tab"
        :to="getExternalLinkList(BCP_47_LOCALES.fr_FR).SUPPORT_CENTER_LINK"
      />
    </RevTabs>

    <RevTabs
      v-if="onboardingCompleted"
      class="mx-8"
      :label="i18n(translations.tabsLabel)"
      role="navigation"
      scrollable
      variant="flexible"
    >
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.HOME.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.HOME.INDEX)"
        class="capitalize"
        :data-qa="translations.home.id"
        :label="i18n(translations.home)"
        role="tab"
        :to="{
          name: ROUTE_NAMES.HOME.INDEX,
        }"
      />
      <RevTabItem
        v-if="
          shouldShow(
            ROUTE_NAMES.INSIGHTS.INDEX,
            PERMISSION_TYPES.EDIT,
            'insightsEnabled',
          )
        "
        :active="isActiveTab(ROUTE_NAMES.INSIGHTS.INDEX)"
        class="capitalize"
        :data-qa="translations.insights.id"
        :label="i18n(translations.insights)"
        role="tab"
        :to="{
          name: ROUTE_NAMES.INSIGHTS.INDEX,
          query: { tableau: TAB_HOME },
        }"
      />
      <RevTabItem
        v-if="
          shouldShow(ROUTE_NAMES.CUSTOMER_CARE.INDEX, PERMISSION_TYPES.EDIT)
        "
        :active="isActiveTab(ROUTE_NAMES.CUSTOMER_CARE.INDEX)"
        class="capitalize"
        :data-qa="translations.sav.id"
        :label="i18n(translations.sav)"
        role="tab"
        :to="{ name: ROUTE_NAMES.CUSTOMER_CARE.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.LISTINGS.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(getListingsRoute())"
        class="capitalize"
        :data-qa="translations.listings.id"
        :label="i18n(translations.listings)"
        role="tab"
        :to="{ name: getListingsRoute() }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.ORDERS.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.ORDERS.INDEX)"
        class="capitalize"
        :data-qa="translations.orders.id"
        :label="i18n(translations.orders)"
        role="tab"
        :to="{ name: ROUTE_NAMES.ORDERS.INDEX }"
      />
      <RevTabItem
        v-if="
          shouldShow(ROUTE_NAMES.OPPORTUNITIES.INDEX, PERMISSION_TYPES.EDIT)
        "
        :active="isActiveTab(ROUTE_NAMES.OPPORTUNITIES.INDEX)"
        class="capitalize"
        :data-qa="translations.opportunities.id"
        :label="i18n(translations.opportunities)"
        role="tab"
        :to="{ name: ROUTE_NAMES.OPPORTUNITIES.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.INVOICES.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.INVOICES.INDEX)"
        class="capitalize"
        :data-qa="translations.invoices.id"
        :label="i18n(translations.invoices)"
        role="tab"
        :to="{ name: ROUTE_NAMES.INVOICES.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.OPTIONS.INDEX, PERMISSION_TYPES.EDIT)"
        :active="isActiveTab(ROUTE_NAMES.OPTIONS.INDEX)"
        class="capitalize"
        :data-qa="translations.options.id"
        :label="i18n(translations.options)"
        role="tab"
        :to="{ name: ROUTE_NAMES.OPTIONS.INDEX }"
      />
      <RevTabItem
        v-if="shouldShow(ROUTE_NAMES.SUPPORT)"
        :active="isActiveTab(ROUTE_NAMES.SUPPORT)"
        class="capitalize"
        :data-qa="translations.support.id"
        :label="i18n(translations.support)"
        role="tab"
        :to="getExternalLinkList(BCP_47_LOCALES.fr_FR).SUPPORT_CENTER_LINK"
      />
      <RevTabItem
        v-if="shouldShowSupportAi"
        :active="false"
        class="capitalize"
        label="Support AI"
        role="tab"
        @click="handleClickSupportAi"
      >
        <RevTag class="align-top" label="Beta" size="small" variant="success" />
      </RevTabItem>
    </RevTabs>
    <ClientOnly v-if="shouldShowSupportAi">
      <AIConversation :name="AI_DISCUSSION_DRAWER" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed, defineAsyncComponent } from 'vue'

import { BCP_47_LOCALES } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'
import { RevTag } from '@ds/components/Tag'

import { usePermissions } from '~/composables/usePermissions'
import { getExternalLinkList } from '~/constants/externalLinks'
import {
  PERMISSION_TYPES,
  type ScopePermissionType,
} from '~/constants/permissions'
import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import {
  type BusinessFeaturesKeys,
  type TemporaryBusinessFeaturesKeys,
  useBusinessFeaturesStore,
} from '~/scopes/config/stores/businessFeaturesStore'
import { TAB_HOME } from '~/scopes/insights/pages/Insights.constants'
import { useListingPagePreference } from '~/scopes/listings/pages/listingsV2/composables/useListingPagePreference'

import translations from './TabsSales.translations'

const AIConversation = defineAsyncComponent(
  () => import('./AIConversation/AIConversation.vue'),
)

const AI_DISCUSSION_DRAWER = 'AI_DISCUSSION_DRAWER'
const i18n = useI18n()
const currentRoute = useRoute()
const { FF_BO_SELLER_ENABLE_AI_CONVERSATION } = useRuntimeConfig().public
const { isFeatureEnabled } = useBusinessFeaturesStore()
const { hasRouteAccess } = usePermissions()
const { onboardingCompleted } = useSellerInfoStore()

const tracking = useTracking()

const shouldShowSupportAi = computed(
  () =>
    FF_BO_SELLER_ENABLE_AI_CONVERSATION &&
    isFeatureEnabled('isAiChatAgentEnabled'),
)

const { isListingsPageV2Default } = useListingPagePreference()

function getListingsRoute() {
  return isListingsPageV2Default.value
    ? ROUTE_NAMES.LISTINGSV2.INDEX
    : ROUTE_NAMES.LISTINGS.INDEX
}

function getOnboardingListingsRoute() {
  return isListingsPageV2Default.value
    ? ROUTE_NAMES.ONBOARDING.LISTINGSV2.INDEX
    : ROUTE_NAMES.ONBOARDING.LISTINGS.INDEX
}

function isActiveTab(routeName: string) {
  return currentRoute.matched.some((route) => route.name === routeName)
}

function shouldShow(
  routeName: string,
  permission?: ScopePermissionType,
  feature?: BusinessFeaturesKeys | TemporaryBusinessFeaturesKeys,
) {
  const shouldShowBasedOnAssociatedFeature =
    !feature || isFeatureEnabled(feature)

  const shouldShowBasedOnPermissions =
    !permission || hasRouteAccess({ name: routeName }, permission)

  return shouldShowBasedOnAssociatedFeature && shouldShowBasedOnPermissions
}

function handleClickSupportAi() {
  tracking.trackClick({
    name: 'support_ai_opened',
    page_type: currentRoute.name as string,
  })
  openModal(AI_DISCUSSION_DRAWER)
}
</script>
